import { Injectable, Type } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { FieldType } from '../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../core/profiles/entities/extended-profile-field.entity';
import { ShFacesListInputFieldComponent } from '../../facial-recognition/components/faces-list-input-field/faces-list-input-field.component';
import { ShFacesListReadonlyFieldComponent } from '../../facial-recognition/components/faces-list-readonly-field/faces-list-readonly-field.component';
import { FormValue, FormValueState } from '../../form-controls/entities/form-value.entity';
import { ShBaseGenericInputFieldComponent } from '../components/input/base-generic-input-field/base-generic-input-field.component';
import { ShListInputFieldComponent } from '../components/input/complex-input-fields/list-input-field/list-input-field.component';
import { ShMultiItemInputFieldComponent } from '../components/input/complex-input-fields/multi-item-input-field/multi-item-input-field.component';
import { GenericInputFieldDescriptor } from '../components/input/generic-input-field/generic-input-field.component';
import { ShBooleanInputFieldComponent } from '../components/input/simple-input-fields/boolean-input-field/boolean-input-field.component';
import { ShDateInputFieldComponent } from '../components/input/simple-input-fields/date-input-field/date-input-field.component';
import { ShEDTFInputFieldComponent } from '../components/input/simple-input-fields/edtf-input-field/edtf-input-field.component';
import { ShEnumInputFieldComponent } from '../components/input/simple-input-fields/enum-input-field/enum-input-field.component';
import { ShFramesInputFieldComponent } from '../components/input/simple-input-fields/frames-input-field/frames-input-field.component';
import { ShLargeTextInputFieldComponent } from '../components/input/simple-input-fields/large-text-input-field/large-text-input-field.component';
import { ShLongInputFieldComponent } from '../components/input/simple-input-fields/long-input-field/long-input-field.component';
import { ShSimpleInputFieldComponent } from '../components/input/simple-input-fields/simple-input-field/simple-input-field.component';
import { ShTimeCodeInputFieldComponent } from '../components/input/simple-input-fields/time-code-input-field/time-code-input-field.component';
import { ShUnsupportedInputFieldComponent } from '../components/input/simple-input-fields/unsupported-input-field/unsupported-input-field.component';
import { ShBaseGenericReadonlyFieldComponent } from '../components/readonly/base-generic-readonly-field/base-generic-readonly-field.component';
import { ShListReadonlyFieldComponent } from '../components/readonly/complex-readonly-fields/list-readonly-field/list-readonly-field.component';
import { ShMapReadonlyFieldComponent } from '../components/readonly/complex-readonly-fields/map-readonly-field/map-readonly-field.component';
import { ShMultiItemReadonlyFieldComponent } from '../components/readonly/complex-readonly-fields/multi-item-readonly-field/multi-item-readonly-field.component';
import { GenericReadonlyFieldDescriptor } from '../components/readonly/generic-readonly-field/generic-readonly-field.component';
import { ShBooleanReadonlyFieldComponent } from '../components/readonly/simple-readonly-fields/boolean-readonly-field/boolean-readonly-field.component';
import { ShDateReadonlyFieldComponent } from '../components/readonly/simple-readonly-fields/date-readonly-field/date-readonly-field.component';
import { ShEnumReadonlyFieldComponent } from '../components/readonly/simple-readonly-fields/enum-readonly-field/enum-readonly-field.component';
import { ShFramesReadonlyFieldComponent } from '../components/readonly/simple-readonly-fields/frames-readonly-field/frames-readonly-field.component';
import { ShLargeTextReadonlyFieldComponent } from '../components/readonly/simple-readonly-fields/large-text-readonly-field/large-text-readonly-field.component';
import { ShLongReadonlyFieldComponent } from '../components/readonly/simple-readonly-fields/long-readonly-field/long-readonly-field.component';
import { ShSimpleReadonlyFieldComponent } from '../components/readonly/simple-readonly-fields/simple-readonly-field/simple-readonly-field.component';
import { ShTimeCodeReadonlyFieldComponent } from '../components/readonly/simple-readonly-fields/time-code-readonly-field/time-code-readonly-field.component';
import { ShUnsupportedReadonlyFieldComponent } from '../components/readonly/simple-readonly-fields/unsupported-readonly-field/unsupported-readonly-field.component';
import { ComponentType } from '../entities/component-types/component-types.entity';
import { DefaultInputMapping, DefaultReadonlyMapping } from '../entities/field-mapping/field-mapping.entity';
import { OverrideDescriptor } from '../entities/override-descriptors/override-descriptor.entity';
import { ShLabeledUrlReadonlyFieldComponent, ShUrlReadonlyFieldComponent } from '../profile-form.module';

@Injectable({ providedIn: 'root' })
export class ShProfileConfigService {
    // #endregion

    // #region Readonly

    /*
     * Used by ShProfileReadonlyFieldsetComponent to retrieve Readonly default mappings.
     *
     *  Default mapping can be overridden.
     *  This way we can map to custom ReadonlyFieldComponents.
     *
     * @returns object mapping fields to a GenericReadonlyFieldDescriptor
     */
    public defaultReadonlyMapping: Record<FieldType, Type<ShBaseGenericReadonlyFieldComponent>> =
        DefaultReadonlyMapping;

    /*
     * Used by ShProfileReadonlyFieldsetComponent to retrieve Readonly overrides.
     *
     * Default value:
     * {
     *   [ComponentType.Address]: ShUnsupportedReadonlyFieldComponent,
     *   [ComponentType.Boolean]: ShBooleanReadonlyFieldComponent,
     *   [ComponentType.Date]: ShDateReadonlyFieldComponent,
     *   [ComponentType.Edtf]: ShEnumReadonlyFieldComponent,
     *   [ComponentType.Enum]: ShEnumReadonlyFieldComponent,
     *   [ComponentType.Frames]: ShFramesReadonlyFieldComponent,
     *   [ComponentType.Jdn]: ShSimpleReadonlyFieldComponent,
     *   [ComponentType.LabeledUrl]: ShLabeledUrlReadonlyFieldComponent,
     *   [ComponentType.List]: ShListReadonlyFieldComponent,
     *   [ComponentType.MapField]: ShMapReadonlyFieldComponent,
     *   [ComponentType.MultiItem]: ShMultiItemReadonlyFieldComponent,
     *   [ComponentType.Number]: ShLongReadonlyFieldComponent,
     *   [ComponentType.Permissions]: ShUnsupportedReadonlyFieldComponent,
     *   [ComponentType.Text]: ShSimpleReadonlyFieldComponent,
     *   [ComponentType.TextArea]: ShLargeTextReadonlyFieldComponent,
     *   [ComponentType.TimeCode]: ShTimeCodeReadonlyFieldComponent,
     *   [ComponentType.Tags]: ShListReadonlyFieldComponent,
     *   [ComponentType.Thesaurus]: ShUnsupportedReadonlyFieldComponent,
     *   [ComponentType.Url]: ShUrlReadonlyFieldComponent,
     *   [ComponentType.WebserviceList]: ShUnsupportedReadonlyFieldComponent
     * };
     *
     *  Example:
     *  We want SimpleField 'LongDescription' to display as a text-area, so in the config provided by 'getInputOverridesConfig'.
     *  So we provided the override with type property ComponentType.TextArea through our getInputOverridesConfig implementation.
     *
     *  Default mapping can be overridden.
     *  This way we can map to custom ReadonlyFieldComponents.
     *
     * @returns object mapping fields to a GenericReadonlyFieldDescriptor
     */
    public overrideReadonlyMapping: Record<string, Type<ShBaseGenericReadonlyFieldComponent>> = {
        [ComponentType.Address]: ShUnsupportedReadonlyFieldComponent,
        [ComponentType.Boolean]: ShBooleanReadonlyFieldComponent,
        [ComponentType.Date]: ShDateReadonlyFieldComponent,
        [ComponentType.Edtf]: ShEnumReadonlyFieldComponent,
        [ComponentType.Enum]: ShEnumReadonlyFieldComponent,
        [ComponentType.Frames]: ShFramesReadonlyFieldComponent,
        [ComponentType.Jdn]: ShSimpleReadonlyFieldComponent,
        [ComponentType.LabeledUrl]: ShLabeledUrlReadonlyFieldComponent,
        [ComponentType.List]: ShListReadonlyFieldComponent,
        [ComponentType.MapField]: ShMapReadonlyFieldComponent,
        [ComponentType.MultiItem]: ShMultiItemReadonlyFieldComponent,
        [ComponentType.Number]: ShLongReadonlyFieldComponent,
        [ComponentType.Permissions]: ShUnsupportedReadonlyFieldComponent,
        [ComponentType.Text]: ShSimpleReadonlyFieldComponent,
        [ComponentType.TextArea]: ShLargeTextReadonlyFieldComponent,
        [ComponentType.TimeCode]: ShTimeCodeReadonlyFieldComponent,
        [ComponentType.Tags]: ShListReadonlyFieldComponent,
        [ComponentType.Thesaurus]: ShUnsupportedReadonlyFieldComponent,
        [ComponentType.Url]: ShUrlReadonlyFieldComponent,
        [ComponentType.WebserviceList]: ShUnsupportedReadonlyFieldComponent,
        [ComponentType.AiFaces]: ShFacesListReadonlyFieldComponent
    };
    // #region Input

    /*
     * Default FieldType to Input-component mapping.
     * Whereas overrideInputMapping (see below) can be used to provide an override
     * for specific field definitions, this can be overridden to provide a custom component
     * for entire field types
     *
     *  Default mapping can be overridden.
     *  This way we can map FieldTypes to custom InputFieldComponents.
     */
    public defaultInputMapping: Record<FieldType, Type<ShBaseGenericInputFieldComponent>> = DefaultInputMapping;

    /*
     *  Default ComponentType to Input-component mapping.
     *  For future use with the 'Control' property of a ProfileField.
     *  Setting this value to a ComponentType value should override the default to use the corresponding custom Input component
     *  At the moment MediaHaven uses this to map to certain client-specific controls, but with config located in the environment.json.
     *
     *  Example:
     *  We want SimpleField 'LongDescription' to display as a text-area,
     *  which would be specified in the config provided by 'getInputOverridesConfig'.
     *  So we provided the override with type property ComponentType.TextArea through our getInputOverridesConfig implementation.
     *
     *  Default mapping can be overridden.
     *  This way we can map to custom InputFieldComponents.
     */
    public overrideInputMapping: Record<string, Type<ShBaseGenericInputFieldComponent>> = {
        [ComponentType.Text]: ShSimpleInputFieldComponent,
        [ComponentType.TextArea]: ShLargeTextInputFieldComponent,
        [ComponentType.Boolean]: ShBooleanInputFieldComponent,
        [ComponentType.Number]: ShLongInputFieldComponent,
        [ComponentType.Enum]: ShEnumInputFieldComponent,
        [ComponentType.Date]: ShDateInputFieldComponent,
        [ComponentType.TimeCode]: ShTimeCodeInputFieldComponent,
        [ComponentType.Edtf]: ShEDTFInputFieldComponent,
        [ComponentType.Frames]: ShFramesInputFieldComponent,
        [ComponentType.List]: ShListInputFieldComponent,
        [ComponentType.MultiItem]: ShMultiItemInputFieldComponent,
        [ComponentType.MapField]: ShUnsupportedInputFieldComponent,
        [ComponentType.Address]: ShUnsupportedInputFieldComponent,
        [ComponentType.Thesaurus]: ShUnsupportedInputFieldComponent,
        [ComponentType.Permissions]: ShUnsupportedInputFieldComponent,
        [ComponentType.Tags]: ShListInputFieldComponent,
        [ComponentType.WebserviceList]: ShUnsupportedInputFieldComponent,
        [ComponentType.Jdn]: ShUnsupportedInputFieldComponent,
        [ComponentType.AiFaces]: ShFacesListInputFieldComponent
    };

    /**
     * A function that is used to retrieve the defaultInputMappings
     * Can be overridden to easily overwrite existing mappings
     *
     * Example:
     * Say we want to provide an implementation for ComplexListFields, then we can implement this
     * function in our extended profile service as follows:
     *
     * public getDefaultInputMappings(): Record<string, Type<ShBaseGenericInputFieldComponent>> {
     *    return { ...this.defaultInputMapping, [FieldType.ComplexListField]: MyComplexListFieldComponent };
     * }
     * @returns the defaultInputMappings
     */
    public getDefaultInputMappings(): Record<FieldType, Type<ShBaseGenericInputFieldComponent>> {
        return this.defaultInputMapping;
    }

    /*
     * Used by ShProfileInputFieldsetComponent to retrieve Input overrides.
     * Calls getInputOverridesConfig inherently which can be used to provide client-specific overrides (including config).
     * Should be expanded to get control overrides through the 'control' property of ProfileFields.
     *
     * NOTE: This function should not be overridden! This is used inherently by the Input components .
     *
     * @returns Observable object mapping fields to a GenericInputFieldDescriptor
     */
    public getInputOverrides(): Observable<Record<string, GenericInputFieldDescriptor>> {
        return this.getInputOverridesConfig().pipe(
            map(overrides => this.mapInputOverrideConfigToComponents(overrides))
        );
    }

    /*
     * Function that can be manually overridden in extended services.
     * Used to manually map certain Fields to certain controls.
     * Can be used if the default mapping based on FieldType is not wanted or you want to map to an application-specific control.
     *
     * This function should be overridden in an extended service to, for example, retrieve mapping from the environment-config.
     *
     * Example:
     * Permissions has its own control in MediaHaven and we want to use a TextArea for SimpleField LongDescription instead of single line text-input.
     * const overrides: Record<string, OverrideDescriptor> = {
     *      FlatKey: { Type: ComponentType },
     *      Permissions: { type: ComponentType.Permissions },
     *      LongDescription: { type: ComponentType.TextArea }
     *  };
     *
     * @returns manually overridden input descriptors
     */
    protected getInputOverridesConfig(): Observable<Record<string, OverrideDescriptor>> {
        return of({});
    }

    private mapInputOverrideConfigToComponents(
        OverrideDescriptors: Record<string, OverrideDescriptor>
    ): Record<string, GenericInputFieldDescriptor> {
        const mappedOverrides: Record<string, GenericInputFieldDescriptor> = {};

        Object.keys(OverrideDescriptors).forEach(key => {
            const override: OverrideDescriptor = OverrideDescriptors[key];
            const mappedType: Type<ShBaseGenericInputFieldComponent> = this.overrideInputMapping[override.type];
            const mappedOverride: GenericInputFieldDescriptor = {
                ...OverrideDescriptors[key],
                type: mappedType
            };

            mappedOverrides[key] = mappedOverride;
        });

        Object.keys(this.overrideInputMapping).forEach(key => {
            const mappedType: Type<ShBaseGenericInputFieldComponent> = this.overrideInputMapping[key];
            const mappedOverride: GenericInputFieldDescriptor = {
                type: mappedType
            };

            mappedOverrides[key] = mappedOverride;
        });

        return mappedOverrides;
    }

    /**
     * This function is called by ShProfileInputFieldsetComponent whenever a change happens on a field (for all fields) and returns whether that field should be hidden.
     * This can be used to perform live logic to hide a field based on the value of another field.
     *
     * Example: Hide Mh2Reuse input field when Mh2Gdpr value is 'Disallowed'
     * const hidden: boolean =
     *      field.FieldDefinition.FlatKey === 'Mh2Reuse' &&
     *      formGroup.value.hasOwnProperty('Dynamic.Mh2Gdpr') &&
     *      formGroup.value.hasOwnProperty('Dynamic.Mh2Reuse') &&
     *      this.formGroupHasValue(formGroup, 'Dynamic.Mh2Gdpr', 'Disallowed');
     *
     * @param field ExtendedProfileField to perform check for
     * @param formGroup Full FormGroup of the Profile
     * @returns
     */
    public isVisuallyHidden(field: ExtendedBaseProfileField, formGroup: UntypedFormGroup): boolean {
        return false;
    }

    /**
     * This function determines whether the user has permission to view
     * profile fields with scope: Standard.
     * Intended to be used in conjunction with a userFunction check
     * @returns
     */
    public canViewStandard(): Observable<boolean> {
        return of(false);
    }

    /**
     * This function determines whether the user has permission to edit
     * profile fields with scope: Standard.
     * Intended to be used in conjunction with a userFunction check
     * @returns
     */
    public canEditStandard(): Observable<boolean> {
        return of(false);
    }

    /**
     * This function determines whether the user has permission to view
     * profile fields with scope: Advanced.
     * Intended to be used in conjunction with a userFunction check
     * @returns
     */
    public canViewAdvanced(): Observable<boolean> {
        return of(false);
    }

    /**
     * This function determines whether the user has permission to edit
     * profile fields with scope: Advanced.
     * Intended to be used in conjunction with a userFunction check
     * @returns
     */
    public canEditAdvanced(): Observable<boolean> {
        return of(false);
    }

    protected formGroupHasValue(formGroup: UntypedFormGroup, key: string, value: string): boolean {
        const res: any | undefined = this.getControlValue(formGroup, key).find(
            el => el.state === FormValueState.SET && el.value === value
        );
        return typeof res !== 'undefined';
    }

    protected getControlValue(formGroup: UntypedFormGroup, key: string): FormValue<any>[] {
        return formGroup.value[key] as FormValue<any>[];
    }

    /**
     * A function that is used to retrieve the defaultReadonlyMappings
     * Can be overridden to easily overwrite existing mappings
     *
     * Example:
     * Say we want to provide an implementation for ComplexListFields, then we can implement this
     * function in our extended profile service as follows:
     *
     * public getDefaultReadOnlyMappings(): Record<string, Type<ShBaseGenericReadonlyFieldComponent>> {
     *    return { ...this.defaultReadonlyMapping, [FieldType.ComplexListField]: MyComplexListFieldComponent };
     * }
     * @returns the defaultReadonlyMappings
     */
    public getDefaultReadOnlyMappings(): Record<FieldType, Type<ShBaseGenericReadonlyFieldComponent>> {
        return this.defaultReadonlyMapping;
    }

    /*
     * Used by ShProfileReadonlyFieldsetComponent to retrieve Readonly overrides.
     * Calls getReadonlyOverridesConfig inherently which can be used to provide client-specific overrides (including config).
     * Should be expanded to get control overrides through the 'control' property of ProfileFields.
     *
     * NOTE: This function should not be overridden! This is used inherently by the Readonly components .
     *
     * @returns Observable object mapping fields to a GenericReadonlyFieldDescriptor
     */
    public getReadonlyOverrides(): Observable<Record<string, GenericReadonlyFieldDescriptor>> {
        return this.getReadonlyOverridesConfig().pipe(
            map(overrides => this.mapReadonlyOverrideConfigToComponents(overrides))
        );
    }

    /*
     * Function that can be manually overridden in extended services.
     * Used to manually map certain Fields to certain readonly elements.
     * Can be used if the default mapping based on FieldType is not wanted or you want to map to an application-specific element.
     *
     * This function should be overridden in an extended service to, for example, retrieve mapping from the environment-config.
     *
     * Example:
     * Permissions has its own control in MediaHaven and we want to use a TextArea for SimpleField LongDescription instead of single line text-input.
     * const overrides: Record<string, OverrideDescriptor> = {
     *      FlatKey: { Type: ComponentType },
     *      Permissions: { type: ComponentType.Permissions },
     *      LongDescription: { type: ComponentType.TextArea }
     *  };
     *
     * @returns manually overridden readonly descriptors
     */
    protected getReadonlyOverridesConfig(): Observable<Record<string, OverrideDescriptor>> {
        return of({});
    }

    private mapReadonlyOverrideConfigToComponents(
        OverrideDescriptors: Record<string, OverrideDescriptor>
    ): Record<string, GenericReadonlyFieldDescriptor> {
        const mappedOverrides: Record<string, GenericReadonlyFieldDescriptor> = {};

        Object.keys(OverrideDescriptors).forEach(key => {
            const override: OverrideDescriptor = OverrideDescriptors[key];
            const mappedType: Type<ShBaseGenericReadonlyFieldComponent> = this.overrideReadonlyMapping[override.type];
            const mappedOverride: GenericReadonlyFieldDescriptor = {
                ...OverrideDescriptors[key],
                type: mappedType
            };

            mappedOverrides[key] = mappedOverride;
        });

        Object.keys(this.overrideReadonlyMapping).forEach(key => {
            const mappedType: Type<ShBaseGenericReadonlyFieldComponent> = this.overrideReadonlyMapping[key];
            const mappedOverride: GenericReadonlyFieldDescriptor = {
                type: mappedType
            };

            mappedOverrides[key] = mappedOverride;
        });

        return mappedOverrides;
    }
    // #endregion
}
